"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { 
  HomeIcon, ListBulletIcon, CalendarDaysIcon, BanknotesIcon, 
  EnvelopeOpenIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  Bars3Icon, XMarkIcon, ArrowPathIcon, UserGroupIcon, TrophyIcon, ArchiveBoxIcon
} from "@heroicons/react/24/outline";
import { Toaster } from "react-hot-toast";

export default function SidebarLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // Elenco delle pagine pubbliche che non richiedono il token
  const publicPages = ['/login', '/register', '/forgot-password', '/reset-password'];

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    const isAuthPage = publicPages.includes(pathname);
    const token = localStorage.getItem("token");

    if (!token && !isAuthPage) {
      router.push("/login");
    } else {
      setIsAuthChecking(false);
    }
  }, [pathname, router]);

  // Schermata nera temporanea mentre il check è in corso
  if (isAuthChecking) {
    return <div className="min-h-screen bg-gray-900"></div>;
  }

  // Se siamo in una pagina pubblica, non renderizziamo la barra laterale
  if (publicPages.includes(pathname)) return <>{children}</>;

  const handleLogout = () => {
    localStorage.removeItem("token");
    router.push("/login");
  };

  const navGroups = [
    {
      title: "Principale",
      items: [
        { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
        { name: "Transazioni", href: "/transactions", icon: ListBulletIcon },
      ]
    },
    {
      title: "Pianificazione",
      items: [
        { name: "Calendario", href: "/calendar", icon: CalendarDaysIcon },
        { name: "Abbonamenti", href: "/recurring", icon: ArrowPathIcon },
        { name: "Fondi & Buste", href: "/envelopes", icon: EnvelopeOpenIcon },
        { name: "Debiti & Crediti", href: "/debts", icon: UserGroupIcon },
        { name: "Obiettivi", href: "/goals", icon: TrophyIcon },
      ]
    },
    {
      title: "Chiusure",
      items: [
        { name: "Bilancio", href: "/balance", icon: BanknotesIcon },
      ]
    },
    {
      title: "Sistema",
      items: [
        { name: "Anagrafiche", href: "/data", icon: ArchiveBoxIcon },
        { name: "Impostazioni", href: "/settings", icon: Cog6ToothIcon },
      ]
    }
  ];

  // Elementi per la barra in basso su Mobile
  const bottomNavItems = [
    { name: "Home", href: "/dashboard", icon: HomeIcon },
    { name: "Spese", href: "/transactions", icon: ListBulletIcon },
    { name: "Buste", href: "/envelopes", icon: EnvelopeOpenIcon },
  ];

  const renderNavLinks = () => (
    <div className="flex flex-col gap-6 mt-6 flex-1 overflow-y-auto scrollbar-hide">
      {navGroups.map((group, index) => (
        <div key={index}>
          <div className="px-4 text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">
            {group.title}
          </div>
          <div className="flex flex-col gap-1 px-2">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = pathname.startsWith(item.href); 
              
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all relative overflow-hidden group
                    ${isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm' 
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white border border-transparent'}`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-2/3 bg-emerald-500 rounded-r-full shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
                  )}
                  <Icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'text-emerald-400 scale-110' : 'text-gray-500 group-hover:scale-110'}`} />
                  <span className="relative z-10">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-900 text-white font-sans">
      
      {/* === MENU MOBILE (Overlay "Altro") === */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden animate-in fade-in duration-200">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          
          <div className="relative w-72 max-w-[85vw] bg-gray-900 border-r border-gray-700 h-full flex flex-col p-4 shadow-2xl animate-in slide-in-from-left duration-300">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
                Menu
              </span>
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            {renderNavLinks()}
            <div className="mt-4 pt-4 border-t border-gray-800">
                <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-400 hover:text-white hover:bg-red-500/20 transition-colors p-3 rounded-xl border border-transparent hover:border-red-500/30 font-bold">
                <ArrowRightOnRectangleIcon className="w-5 h-5" /> Esci
                </button>
            </div>
          </div>
        </div>
      )}

      {/* === SIDEBAR DESKTOP === */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-gray-900 border-r border-gray-700 shadow-xl z-40">
        <div className="flex flex-col flex-1 min-h-0 p-4">
          <div className="flex items-center h-12 mb-2 px-2 mt-2">
            <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
              Cashflow
            </span>
          </div>
          {renderNavLinks()}
          <div className="mt-4 pt-4 border-t border-gray-800">
              <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-400 hover:text-white hover:bg-red-500/20 transition-colors p-3 rounded-xl border border-transparent hover:border-red-500/30 font-bold">
                <ArrowRightOnRectangleIcon className="w-5 h-5" /> Esci
              </button>
          </div>
        </div>
      </div>

      {/* === CONTENUTO PRINCIPALE === */}
      <div className="flex-1 flex flex-col min-w-0 md:pl-64">
        
        {/* Header Top Mobile (Solo Logo, niente hamburger) */}
        <div className="md:hidden flex items-center justify-center bg-gray-900 p-3 border-b border-gray-700 sticky top-0 z-30 shadow-sm">
          <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
            Cashflow
          </span>
        </div>

        {/* pb-24 serve per non far nascondere il contenuto dalla barra di navigazione inferiore */}
        <main className="flex-1 pb-24 md:pb-0">
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: { background: '#1f2937', color: '#fff', border: '1px solid #374151' },
              success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }} 
          />
          {children}
        </main>
      </div>

      {/* === BOTTOM NAVBAR MOBILE === */}
      {/* Usiamo pb-[env(safe-area-inset-bottom)] per supportare il "baffo" degli iPhone */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-t border-gray-800 flex justify-around items-center h-[68px] z-40 pb-[env(safe-area-inset-bottom)]">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname.startsWith(item.href);
          return (
            <Link key={item.name} href={item.href} className="flex flex-col items-center justify-center w-full h-full gap-1 pt-1">
              <Icon className={`w-6 h-6 transition-transform ${isActive ? 'text-emerald-400 scale-110' : 'text-gray-400'}`} />
              <span className={`text-[10px] font-bold ${isActive ? 'text-emerald-400' : 'text-gray-500'}`}>{item.name}</span>
            </Link>
          );
        })}
        {/* Tasto Menu che apre l'overlay laterale con le altre voci */}
        <button onClick={() => setIsOpen(true)} className="flex flex-col items-center justify-center w-full h-full gap-1 pt-1">
          <Bars3Icon className="w-6 h-6 text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500">Altro</span>
        </button>
      </div>

    </div>
  );
}